import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getAllCategory, getBySlug } from "../../../server/Category";
import FilterSelect from "./FilterSelect";
import CategoryChip from "./CategoryChip";
import CategoryAddPopup from "./CategoryAddPopup";
import useClickOutside from "./useClickOutside";
import { useCategoryMap, buildPath } from "./useCategoryUtils";
import SelectChip from "./SelectChip";
import {
  addCategoryChip,
  removeCategoryChip,
  updateCategoryChip,
  setExtraColumns,
  reload,
} from "../../../server/ProductsFilters";
import { EXSTRA_COLUMNS } from "./ProductsTable.js";
import { getUncategorizedProducts, getProductByProductsIds } from "../../../server/Product";
import showAlert from "../../../server/ShowAlert.js";

const ProductsFilters = ({
  brand,
  subBrand,
  filters,
  setFilters,
  clearFilters,
  setProducts,
  filteredProducts,
  statusFilter,
  setStatusFilter,
  availableStatuses,
  openModal,
}) => {
  const dispatch = useDispatch();

  const [pendingDept, setPendingDept] = useState("");
  const [pendingCat, setPendingCat] = useState("");
  const [pendingSub, setPendingSub] = useState("");
  const categoryChips = useSelector(
    (state) => state.productFilters.filters.categoryChips,
  );
  const extraColumns = useSelector(
    (state) => state.productFilters.extraColumns,
  );
  const [openAddPopup, setOpenAddPopup] = useState(false);

  const addRef = useRef(null);
  useClickOutside(addRef, () => setOpenAddPopup(false));

  const { stationId, categories } = useSelector((state) => ({
    stationId: state.user.stationId,
    categories: state.category.categories,
  }));

  const categoryMap = useCategoryMap(categories);
  const departments = categories;
  const level2 =
    departments.find((c) => c.slug === pendingDept)?.children || [];
  const level3 = level2.find((c) => c.slug === pendingCat)?.children || [];

  const change = (key, value) => setFilters({ [key]: value });

  const addBrandChip = (id) => {
    if (!id || filters.brandIds.includes(id)) return;
    change("brandIds", [...filters.brandIds, id]);
  };

  const removeBrandChip = (id) => {
    change(
      "brandIds",
      filters.brandIds.filter((b) => b !== id),
    );
  };

  const selectAllBrands = (ids) =>
    change("brandIds", [...new Set([...filters.brandIds, ...ids])]);

  const clearAllBrands = (ids) =>
    change(
      "brandIds",
      filters.brandIds.filter((id) => !ids.includes(id)),
    );

  const selectAllSubBrands = (ids) =>
    change("subBrandIds", [...new Set([...filters.subBrandIds, ...ids])]);

  const clearAllSubBrands = (ids) =>
    change(
      "subBrandIds",
      filters.subBrandIds.filter((id) => !ids.includes(id)),
    );

  const addSubBrandChip = (id) => {
    if (!id || filters.subBrandIds.includes(id)) return;
    change("subBrandIds", [...filters.subBrandIds, id]);
  };

  const removeSubBrandChip = (id) => {
    change(
      "subBrandIds",
      filters.subBrandIds.filter((b) => b !== id),
    );
  };

  const toggleExtraColumn = (id) =>
    dispatch(
      extraColumns.includes(id) ? removeExtraColumn(id) : addExtraColumn(id),
    );

  const selectAllExtraColumns = (ids) =>
    dispatch(setExtraColumns([...new Set([...extraColumns, ...ids])]));

  const clearAllExtraColumns = (ids) =>
    dispatch(setExtraColumns(extraColumns.filter((id) => !ids.includes(id))));

  const removeExtraColumn = (id) =>
    dispatch(setExtraColumns(extraColumns.filter((c) => c !== id)));

  const addExtraColumn = (id) =>
    dispatch(setExtraColumns([...extraColumns, id]));

  const addChip = () => {
    const path = buildPath([pendingDept, pendingCat, pendingSub], categoryMap);
    if (!path.length) return false;
    const slug = path[path.length - 1].slug;
    if (categoryChips.some((c) => c.slug === slug)) return false;
    dispatch(addCategoryChip({ slug, path }));
    setPendingDept("");
    setPendingCat("");
    setPendingSub("");
    return true;
  };

  const updateChip = (oldId, newChip) =>
    dispatch(updateCategoryChip(oldId, newChip));

  const removeChip = (id) => dispatch(removeCategoryChip(id));

  const clearAllFilters = () => {
    clearFilters();
    setProducts([]);
  };

  const exportToCSV = () => {
    const escape = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;
    const rows = filteredProducts.map((product) => {
      const p = product.product || product;
      return [
        escape(p.gtin || "-"),
        escape(
          `${p.name} ${p.weightDetails?.netContent} ${p.weightDetails?.unitOfMeasure}`,
        ),
        escape(p.brand?.name),
        escape(p.subBrand?.name),
        escape(product.pricing?.price),
        escape(p.availability?.availableQuantity),
      ];
    });
    const csv = [
      ["ברקוד", "שם מוצר", "ספק",  "מותג", "מחיר", "מלאי זמין"],
      ...rows,
    ]
      .map((r) => r.join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "products.csv";
    link.click();
  };

  const getUncategorized = () => {
    clearFilters();
    dispatch(reload());
    getUncategorizedProducts(stationId)
      .then((res) => {
        const gtins = res.products.map(p => p.product_id).join(",");
        getProductByProductsIds(gtins, stationId).then(res => setProducts(res.data))
      })
      .catch((err) => {
        console.error("Error fetching uncategorized products:", err);
        showAlert({msgType: "error", msgTitle: "אירעה שגיאה בטעינת המוצרים היתומים"});
      });
  };

  const canAdd = !!(pendingDept || pendingCat || pendingSub);

  const brandLabel = (id) => brand.find((b) => b.id === id)?.name ?? id;
  const subBrandLabel = (id) => subBrand.find((b) => b.id === id)?.name ?? id;

  return (
    <>
      <div className="filters-bar">
        <button className="create-btn" onClick={() => openModal()}>
          + יצירת מוצר
        </button>
        <input
          className="search-input"
          placeholder="חיפוש מוצר..."
          value={filters.search}
          onChange={(e) => change("search", e.target.value)}
        />
        <input
          className="search-input"
          placeholder="ברקוד"
          value={filters.gtin}
          onChange={(e) => change("gtin", e.target.value)}
        />

        <FilterSelect
          changeFunc={addSubBrandChip}
          valName="מותג"
          filtersArr={subBrand}
          selectedIds={filters.subBrandIds}
          onRemove={removeSubBrandChip}
          onSelectAll={selectAllSubBrands}
          onClearAll={clearAllSubBrands}
        />

        <FilterSelect
          changeFunc={addBrandChip}
          valName="ספק"
          filtersArr={brand}
          selectedIds={filters.brandIds}
          onRemove={removeBrandChip}
          onSelectAll={selectAllBrands}
          onClearAll={clearAllBrands}
        />

        {availableStatuses.length > 0 && (
          <FilterSelect
            changeFunc={setStatusFilter}
            valName="סטטוס"
            filtersArr={availableStatuses.map((s) => ({ id: s, name: s }))}
            selectedIds={statusFilter ? [statusFilter] : []}
            onRemove={() => setStatusFilter("")}
            onSelectAll={(ids) => setStatusFilter(ids[0])}
            onClearAll={() => setStatusFilter("")}
          />
        )}

        <div className="add-category-wrapper" ref={addRef}>
          <button
            className="add-category-btn"
            onClick={() => setOpenAddPopup((p) => !p)}
          >
            + הוסף קטגוריה
          </button>

          {openAddPopup && (
            <CategoryAddPopup
              departments={departments}
              level2={level2}
              level3={level3}
              pendingDept={pendingDept}
              pendingCat={pendingCat}
              pendingSub={pendingSub}
              setPendingDept={setPendingDept}
              setPendingCat={setPendingCat}
              setPendingSub={setPendingSub}
              canAdd={canAdd}
              onAdd={() => {
                const success = addChip();
                if (success) setOpenAddPopup(false);
              }}
            />
          )}
        </div>

        <button className="clear-btn" onClick={clearAllFilters}>
          נקה פילטרים
        </button>

        {filteredProducts.length !== 0 && (
          <button className="export-btn" onClick={exportToCSV}>
            ייצוא לקובץ
          </button>
        )}

        <FilterSelect
          changeFunc={toggleExtraColumn}
          valName="עמודות נוספות"
          filtersArr={EXSTRA_COLUMNS.map((c) => ({ id: c.key, name: c.label }))}
          selectedIds={extraColumns}
          onRemove={removeExtraColumn}
          onSelectAll={selectAllExtraColumns}
          onClearAll={clearAllExtraColumns}
          hideChips
        />

        <button className="export-btn" onClick={getUncategorized}>
          הצג יתומים
        </button>

        {filteredProducts.length !== 0 && (
          <span>{filteredProducts.length} פריטים</span>
        )}
      </div>

      <div className="chips-row">
        {categoryChips.map((chip) => (
          <CategoryChip
            key={chip.slug}
            chip={chip}
            categoryMap={categoryMap}
            allCategories={categories}
            onUpdate={(newChip) => updateChip(chip.slug, newChip)}
            onRemove={() => removeChip(chip.slug)}
          />
        ))}

        {filters.brandIds.map((chip) => (
          <SelectChip
            key={chip}
            label={brandLabel(chip)}
            onRemove={() => removeBrandChip(chip)}
            colorClass={"brand"}
          />
        ))}

        {filters.subBrandIds.map((chip) => (
          <SelectChip
            key={chip}
            label={subBrandLabel(chip)}
            onRemove={() => removeSubBrandChip(chip)}
            colorClass={"sub-brand"}
          />
        ))}
      </div>
    </>
  );
};

export default ProductsFilters;
